<script setup lang="ts">
    import { cleanHtml } from '@/utils/string';
    import { computed, onMounted, ref } from 'vue';

    type InputMode = 'decimal' | 'email' | 'none' | 'numeric' | 'search' | 'tel' | 'text' | 'url';

    const {
        autocomplete = 'off',
        error = undefined,
        hidden = false,
        inputMode = undefined,
        inputOptions = null,
        label,
        name,
        placeholder = '',
        required = false,
        showLabel = true,
        type = 'text',
        ...props
    } = defineProps<{
        autocomplete?: string;
        disabled?: boolean;
        error?: string;
        focus?: boolean;
        hidden?: boolean;
        inputMode?: InputMode;
        inputOptions?: Record<string, string> | null;
        label: string;
        name: string;
        placeholder?: string;
        required?: boolean;
        showLabel?: boolean;
        type?: 'email' | 'number' | 'password' | 'text';
    }>();

    // eslint-disable-next-line vue/prefer-use-template-ref
    const inputRef = ref<HTMLInputElement>();

    type InputValue = number | string | null | undefined;

    const model = defineModel<InputValue>({
        set(value) {
            if (typeof value === 'string') {
                return cleanHtml(value);
            }
            return value;
        },
    });

    onMounted(() => {
        if (inputRef.value && props.focus) {
            inputRef.value.focus();
        }
    });

    const inputModeComputed = computed<InputMode>(() => {
        // Override input mode if it's explicitly set
        if (inputMode !== undefined) {
            return inputMode;
        }

        // Otherwise, set it based on the type
        if (type === 'email') {
            return 'email';
        }
        if (type === 'number') {
            return 'numeric';
        }

        return 'text';
    });

    function onInput(event: Event): void {
        const rawValue = (event.target as HTMLInputElement)?.value;

        if (rawValue === '') {
            model.value = null;
            return;
        }
        model.value = type === 'number' ? Number(rawValue) : rawValue;
    }
</script>

<template>
    <div>
        <div
            :class="[
                hidden && 'hidden',
                disabled && 'cursor-not-allowed bg-gray-100 text-gray-400',
                !!error && 'border-red-600',
                type !== 'number' && 'p-1',
                'relative',
            ]"
        >
            <label
                v-show="showLabel"
                :for="name"
                class="absolute -top-2 left-2 -mt-px inline-block bg-white px-1 text-xs"
            >
                {{ label }}
                <span
                    v-if="required"
                    class="text-red-600"
                    aria-hidden="true"
                    >*</span
                >
            </label>
            <div class="flex">
                <input
                    :id="name"
                    ref="inputRef"
                    :name
                    :value="modelValue"
                    :disabled
                    :type
                    :required
                    class="block w-full flex-1 cursor-text appearance-none border-0 border-border-primary bg-transparent p-0 placeholder-text focus:outline-none disabled:cursor-not-allowed sm:text-sm"
                    v-bind="inputOptions"
                    :inputmode="inputModeComputed"
                    :autocomplete
                    :placeholder
                    :aria-required="required"
                    :aria-invalid="!!error"
                    :aria-describedBy="`${name}-error`"
                    autocorrect="off"
                    autocapitalize="off"
                    @input="onInput"
                />

                <slot :input-ref />
            </div>
        </div>
        <span
            v-if="!!error"
            :id="`${name}-error`"
            :data-test="`${name}-error`"
            class="font-ro-regular text-red-500 mt-2 text-xs"
            >{{ error }}</span
        >
    </div>
</template>
